// /opt/dashboard/frontend/src/pages/ApiCredits.js
// 💰 API Kreditek — becsült OpenAI / Claude egyenleg (kezdőérték − mért költés)
import React, { useCallback, useEffect, useState } from 'react';

const C = {
  bg: '#1a1d27', panel: '#22252f', panel2: '#2a2e3a', border: '#2a2e3a',
  text: '#e0e6ed', muted: '#8892a0', accent: '#00d4ff',
  ok: '#22c55e', warn: '#fbbf24', bad: '#ef4444', purple: '#8b5cf6',
};

const META = {
  openai: { name: 'OpenAI', icon: '🟢', envHint: 'OPENAI_ADMIN_KEY' },
  anthropic: { name: 'Claude (Anthropic)', icon: '🟠', envHint: 'ANTHROPIC_ADMIN_KEY' },
};

const usd = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);

function ProviderCard({ id, data, onSave, saving }) {
  const meta = META[id];
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');

  useEffect(() => {
    if (data.startAmount != null) setAmount(String(data.startAmount));
    if (data.startDate) setDate(data.startDate);
  }, [data.startAmount, data.startDate]);

  const remaining = data.remaining;
  const remColor = remaining == null ? C.muted : remaining < 5 ? C.bad : remaining < 15 ? C.warn : C.ok;
  const maxDaily = data.daily.length ? Math.max(...data.daily.map((d) => d.usd)) : 1;

  return (
    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>{meta.icon} {meta.name}</div>
        <div style={{ fontSize: 30, fontWeight: 800, color: remColor }}>
          {usd(remaining)}
          <span style={{ fontSize: 12, fontWeight: 400, color: C.muted, marginLeft: 6 }}>becsült egyenleg</span>
        </div>
      </div>

      {!data.configured && !data.error && (
        <div style={{ marginTop: 10, fontSize: 13, color: C.bad }}>
          ⚠️ A {meta.envHint} nincs beállítva a szerveren.
        </div>
      )}
      {data.error && (
        <div style={{ marginTop: 10, fontSize: 13, color: C.bad }}>❌ {data.error}</div>
      )}

      <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap', fontSize: 13, color: C.muted }}>
        <span>Kezdőérték: <strong style={{ color: C.text }}>{usd(data.startAmount)}</strong>{data.startDate ? ` (${data.startDate})` : ''}</span>
        <span>Azóta elköltve: <strong style={{ color: C.text }}>{data.spent == null ? '—' : `$${data.spent.toFixed(4)}`}</strong></span>
      </div>

      {/* Napi költés */}
      {data.daily.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            Napi költés (max. 30 nap)
          </div>
          {data.daily.map((d) => (
            <div key={d.date} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 11, color: C.muted, width: 78, flexShrink: 0 }}>{d.date}</span>
              <div style={{ flex: 1, height: 12, background: C.panel2, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(d.usd / maxDaily) * 100}%`, background: `linear-gradient(90deg, ${C.accent}, ${C.purple})` }} />
              </div>
              <span style={{ fontFamily: 'monospace', fontSize: 11, width: 62, textAlign: 'right', flexShrink: 0 }}>${d.usd.toFixed(3)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Kezdőérték beállítás */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
          Kezdőérték beállítása (feltöltés után frissítsd!)
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: C.muted, fontSize: 13 }}>Ekkor:</span>
          <input
            type="date" value={date} onChange={(e) => setDate(e.target.value)}
            style={{ background: C.panel2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 10px', color: C.text, fontSize: 13 }}
          />
          <span style={{ color: C.muted, fontSize: 13 }}>ennyi volt: $</span>
          <input
            type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
            placeholder="pl. 50"
            style={{ background: C.panel2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 10px', color: C.text, fontSize: 13, width: 90 }}
          />
          <button
            onClick={() => onSave(id, amount, date)}
            disabled={saving || !amount || !date}
            style={{
              background: C.accent, border: 'none', borderRadius: 8, color: '#0a0e1a',
              padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
              opacity: saving || !amount || !date ? 0.5 : 1,
            }}
          >
            {saving ? '⏳' : '💾 Mentés'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ApiCredits() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch('/api/api-credits')
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((j) => { setData(j); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save(provider, amount, date) {
    setSaving(true);
    try {
      const res = await fetch('/api/api-credits/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider, startAmount: Number(amount), startDate: date }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      load();
    } catch (e) {
      alert('Mentés hiba: ' + e.message);
    }
    setSaving(false);
  }

  return (
    <div style={{ color: C.text, maxWidth: 820 }}>
      <h2 style={{ margin: '0 0 4px' }}>💰 API Kreditek</h2>
      <div style={{ color: C.muted, fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
        Egyik szolgáltató sem ad hivatalos egyenleg-endpointot, ezért a becslés: kezdőérték − a hivatalos
        cost API-val mért költés. Feltöltés után írd át a kezdőértéket az aznapi egyenlegre!
      </div>

      {loading && <div style={{ color: C.muted, padding: 20 }}>⏳ Betöltés…</div>}
      {error && (
        <div style={{ background: C.panel, border: `1px solid ${C.bad}`, borderRadius: 12, padding: 16, color: C.bad }}>
          ❌ Hiba: {error}
        </div>
      )}
      {!loading && !error && data && (
        <>
          <ProviderCard id="openai" data={data.providers.openai} onSave={save} saving={saving} />
          <ProviderCard id="anthropic" data={data.providers.anthropic} onSave={save} saving={saving} />
          <button
            onClick={load}
            style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}
          >
            🔄 Frissítés
          </button>
        </>
      )}
      <div style={{ height: 40 }} />
    </div>
  );
}
